import {
  SEO_ENTITY_SECTIONS,
  createEmptySeoState,
  createSeoBlock,
  isEmptySeoBlock
} from '../services/data/seoModel'
import {
  ensureSeoDocuments,
  loadSeo,
  saveSeoEntity,
  saveSeoGlobal,
  saveSeoPage,
  saveSeoScripts
} from '../services/firebase/seoService'
import { notifyActionError } from '../helpers/errorMessages'

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function entityLabel(collectionKey) {
  const section = SEO_ENTITY_SECTIONS.find(item => item.collectionKey === collectionKey)
  return section ? section.single : collectionKey
}

let seoLoadPromise = null

/**
 * Последний рубеж перед записью в SiteSeo: правила Firestore разрешают ее только
 * роли Admin. Если элемент управления где-то остался живым, действие не пойдет в
 * Firestore и покажет понятный текст вместо сырого permission-denied.
 */
function denySeoWrite(dispatch) {
  return dispatch('notify', {
    type: 'warning',
    title: 'Недостаточно прав',
    message: 'Изменение SEO доступно только администратору.'
  }).then(() => null)
}

export default {
  state: {
    data: null,
    loading: false,
    loaded: false,
    error: ''
  },
  getters: {
    seoData: state => state.data,
    seoGlobal: state => (state.data ? state.data.global : createEmptySeoState().global),
    seoPages: state => (state.data ? state.data.pages : {}),
    seoScripts: state => (state.data ? state.data.scripts : []),
    seoEntities: state => (state.data ? state.data.entities : { services: {}, products: {}, articles: {} }),
    seoLoading: state => state.loading,
    seoLoaded: state => state.loaded,
    seoError: state => state.error
  },
  mutations: {
    setSeoLoading(state, value) { state.loading = value },
    setSeoError(state, value) { state.error = value },
    setSeoData(state, data) {
      state.data = data
      state.loaded = true
    },
    setSeoGlobalData(state, globalSeo) {
      if (!state.data) return
      state.data.global = clone(globalSeo)
    },
    setSeoPageData(state, { pageKey, block }) {
      if (!state.data) return
      state.data.pages[pageKey] = clone(block)
    },
    setSeoScriptsData(state, items) {
      if (!state.data) return
      state.data.scripts = clone(items)
    },
    setSeoEntityData(state, { collectionKey, entityId, block }) {
      if (!state.data) return
      if (!state.data.entities[collectionKey]) state.data.entities[collectionKey] = {}
      if (block === null) delete state.data.entities[collectionKey][entityId]
      else state.data.entities[collectionKey][entityId] = clone(block)
    }
  },
  actions: {
    async fetchSeo({ commit, dispatch, getters, state }, payload = {}) {
      if (!payload.force && state.loaded) return state.data
      if (!payload.force && seoLoadPromise) return seoLoadPromise

      commit('setSeoLoading', true)
      commit('setSeoError', '')

      seoLoadPromise = (async () => {
        try {
          // Сидинг создает недостающие документы, то есть пишет в SiteSeo.
          // Для роли без прав на запись его пропускаем, иначе просмотр раздела
          // упадет на permission-denied еще до чтения.
          if (getters.canEditSeo) await ensureSeoDocuments()
          const data = await loadSeo()
          commit('setSeoData', data)
          return data
        } catch (error) {
          commit('setSeoError', error.message || 'Не удалось загрузить коллекцию SiteSeo.')
          await notifyActionError(dispatch, error, 'Ошибка загрузки SEO')
          throw error
        } finally {
          commit('setSeoLoading', false)
          seoLoadPromise = null
        }
      })()

      return seoLoadPromise
    },

    async saveSeoGlobalSettings({ commit, dispatch, getters }, globalSeo) {
      if (!getters.canEditSeo) return denySeoWrite(dispatch)

      try {
        const saved = await saveSeoGlobal(globalSeo)
        commit('setSeoGlobalData', saved)
        await dispatch('notify', { type: 'success', title: 'Общие SEO-настройки сохранены' })
        await dispatch('recordAuditLog', {
          action: 'save_seo_global',
          entityType: 'seo',
          entityId: 'global',
          title: 'SEO: общие настройки',
          details: { document: 'SiteSeo/global' }
        })
        return saved
      } catch (error) {
        await notifyActionError(dispatch, error, 'SEO-настройки не сохранены')
        throw error
      }
    },

    async saveSeoPageBlock({ commit, dispatch, getters }, { pageKey, block, pageLabel }) {
      if (!getters.canEditSeo) return denySeoWrite(dispatch)

      try {
        const saved = await saveSeoPage(pageKey, block)
        commit('setSeoPageData', { pageKey, block: saved })
        await dispatch('notify', { type: 'success', title: 'SEO страницы сохранено' })
        await dispatch('recordAuditLog', {
          action: 'save_seo_page',
          entityType: 'seo',
          entityId: pageKey,
          title: `SEO страницы: ${pageLabel || pageKey}`,
          details: { document: 'SiteSeo/pages', pageKey }
        })
        return saved
      } catch (error) {
        await notifyActionError(dispatch, error, 'SEO страницы не сохранено')
        throw error
      }
    },

    /**
     * Пустой SEO-блок не сохраняется: ключ сущности удаляется из документа,
     * чтобы клиент не тянул лишний вес на каждой загрузке сайта.
     */
    async saveSeoEntityBlock({ commit, dispatch, getters }, { collectionKey, entityId, block, entityTitle }) {
      if (!getters.canEditSeo) return denySeoWrite(dispatch)

      const seoBlock = createSeoBlock(block)
      const shouldRemove = isEmptySeoBlock(seoBlock)
      try {
        await saveSeoEntity(collectionKey, entityId, shouldRemove ? null : seoBlock)
        commit('setSeoEntityData', { collectionKey, entityId, block: shouldRemove ? null : seoBlock })
        await dispatch('notify', {
          type: 'success',
          title: shouldRemove ? 'SEO-переопределение снято' : 'SEO сущности сохранено',
          message: shouldRemove ? 'Все поля пустые — запись удалена, сущность наследует SEO страницы и global.' : ''
        })
        await dispatch('recordAuditLog', {
          action: shouldRemove ? 'reset_seo_entity' : 'save_seo_entity',
          entityType: 'seo',
          entityId,
          title: `SEO: ${entityLabel(collectionKey)} ${entityTitle || entityId}`,
          details: { collectionKey, entityId, removed: shouldRemove }
        })
        return shouldRemove ? null : seoBlock
      } catch (error) {
        await notifyActionError(dispatch, error, shouldRemove ? 'SEO сущности не сброшено' : 'SEO сущности не сохранено')
        throw error
      }
    },

    async saveSeoScriptsList({ commit, dispatch, getters }, items) {
      if (!getters.canEditSeo) return denySeoWrite(dispatch)

      try {
        const saved = await saveSeoScripts(items)
        commit('setSeoScriptsData', saved)
        await dispatch('notify', { type: 'success', title: 'Скрипты сохранены' })
        await dispatch('recordAuditLog', {
          action: 'save_seo_scripts',
          entityType: 'seo',
          entityId: 'scripts',
          title: 'SEO: скрипты и метрики',
          details: { document: 'SiteSeo/scripts', count: saved.length }
        })
        return saved
      } catch (error) {
        await notifyActionError(dispatch, error, 'Скрипты не сохранены')
        throw error
      }
    }
  }
}
